import React, { useState, useEffect, useRef } from 'react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const CreateExceptionDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  selectedLevelId,
  corporateAccount,
  currentProject,
  login_token,
  functionalLevelDescription,
  requirementIdWithPrefix
}) => {
  const [exceptionType, setExceptionType] = useState('ONLY APPLIES TO');
  const [exceptionLevel, setExceptionLevel] = useState('Requirement');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [attributeValues, setAttributeValues] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Add ref for focus management
  const categorySelectRef = useRef(null);

  // Dialog styles (updated to match other dialogs)
  const DialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    dialog: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      width: '700px', // Changed from 700px to match other dialogs
      maxWidth: '90vw',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      maxHeight: '90vh',
      overflowY: 'auto'
    }
  };

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && categorySelectRef.current) {
      categorySelectRef.current.focus();
    }
  }, [isOpen]);

  // Initialize when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      // Reset form state
      setSelectedCategory('');
      setAttributeValues([]);
      setSelectedAttributes([]);
      setExceptionType('ONLY APPLIES TO');
      setError('');

      // Set proper exception level based on what's selected
      if (selectedRequirements.size > 0) {
        setExceptionLevel('Requirement');
      } else if (selectedLevelId) {
        setExceptionLevel('FunctionalLevel');
      }
    }
  }, [isOpen, corporateAccount, currentProject, selectedRequirements, selectedLevelId]);

  // Fetch attributes when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchAttributes(selectedCategory);
      // Clear selected attributes when category changes
      setSelectedAttributes([]);
    } else {
      setAttributeValues([]);
      setSelectedAttributes([]);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(appConfig.getUrl('/get_key_functional_attribute_categories'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setCategories(data.attribute_category_list || []);
      } else {
        throw new Error(data.status_description || 'Failed to fetch categories');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching categories:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAttributes = async (category) => {
    setIsLoading(true);
    setError('');
    // Clear attribute values immediately when starting a new fetch
    setAttributeValues([]);
    setSelectedAttributes([]);

    try {
      const response = await fetch(appConfig.getUrl('/get_key_functional_attributes'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          attribute_category: category,
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Log for debugging
        console.log('Received attributes:', data.attribute_list);

        // Process attribute list to ensure key_attribute_list_id is properly extracted
        const processedAttributes = (data.attribute_list || []).map(attr => {
          // Ensure key_attribute_list_id is properly converted to number if it's a string
          if (typeof attr.key_attribute_list_id === 'string' && attr.key_attribute_list_id.trim() !== '') {
            attr.key_attribute_list_id = parseInt(attr.key_attribute_list_id, 10);
          }
          return attr;
        });

        setAttributeValues(processedAttributes);
      } else {
        throw new Error(data.status_description || 'Failed to fetch attributes');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching attributes:', err);
      // Ensure attributes are cleared on error as well
      setAttributeValues([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate exception level selection
    if (exceptionLevel === 'FunctionalLevel' && !selectedLevelId) {
      setError('Please select a functional level');
      setIsLoading(false);
      return;
    }

    if (exceptionLevel === 'Requirement' && selectedRequirements.size === 0) {
      setError('Please select at least one requirement');
      setIsLoading(false);
      return;
    }

    // Validate form inputs
    if (!selectedCategory) {
      setError('Please select an exception category');
      setIsLoading(false);
      return;
    }

    if (selectedAttributes.length === 0) {
      setError('Please select at least one exception value');
      setIsLoading(false);
      return;
    }

    // Validate that we have valid IDs for attributes
    const validAttributeIds = selectedAttributes
      .filter(attr => attr.key_attribute_list_id !== undefined && attr.key_attribute_list_id !== 0)
      .map(attr => attr.key_attribute_list_id.toString());

    if (validAttributeIds.length === 0) {
      setError('Cannot proceed: Selected attributes do not have valid IDs');
      setIsLoading(false);
      return;
    }

    console.log('Sending attribute IDs:', validAttributeIds);
    console.log('Exception level:', exceptionLevel);
    console.log('Selected requirements:', Array.from(selectedRequirements));
    console.log('Selected level ID:', selectedLevelId);

    try {
      const response = await fetch(appConfig.getUrl('/add_key_attributes_list_requirements'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: exceptionLevel === 'FunctionalLevel' ? selectedLevelId : '',
          req_ids: exceptionLevel === 'Requirement' ? Array.from(selectedRequirements) : [],
          key_attribute_list_ids: selectedAttributes
            .filter(attr => attr.key_attribute_list_id !== undefined && attr.key_attribute_list_id !== 0)
            .map(attr => attr.key_attribute_list_id.toString()),
          include_exclude: exceptionType === 'ONLY APPLIES TO' ? 'INCLUDE' : 'EXCLUDE'
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setError('Exception created successfully');

        // Delay the close to show success message
        setTimeout(() => {
          onClose(true);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to create exception');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error creating exception:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel button click - explicitly pass false to indicate no success
  const handleCancel = () => {
    onClose(false);
  };

  const toggleAttributeSelection = (attribute) => {
    // Create a unique identifier that works reliably
    const attributeId = attribute.key_attribute_list_id !== undefined && attribute.key_attribute_list_id !== 0
      ? attribute.key_attribute_list_id
      : attribute.attribute_name;

    // Check if this attribute is already selected using the unique identifier
    const isSelected = selectedAttributes.some(attr => {
      const selectedId = attr.key_attribute_list_id !== undefined && attr.key_attribute_list_id !== 0
        ? attr.key_attribute_list_id
        : attr.attribute_name;
      return selectedId === attributeId;
    });

    if (isSelected) {
      // Remove the attribute from selection
      setSelectedAttributes(selectedAttributes.filter(attr => {
        const selectedId = attr.key_attribute_list_id !== undefined && attr.key_attribute_list_id !== 0
          ? attr.key_attribute_list_id
          : attr.attribute_name;
        return selectedId !== attributeId;
      }));
    } else {
      // Add the attribute to selection
      setSelectedAttributes([...selectedAttributes, attribute]);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h2 style={{
          backgroundColor: '#CCCCFF',
          textAlign: 'center',
          padding: '10px',
          margin: '-20px -20px 20px -20px',
          borderRadius: '8px 8px 0 0',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Create New Exception
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '12px'
            }}>
              At what level would you like to create this new exception?:
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: !selectedLevelId ? 0.6 : 1
              }}>
                <input
                  type="radio"
                  name="exceptionLevel"
                  style={{ width: '20px', height: '20px' }}
                  checked={exceptionLevel === 'FunctionalLevel'}
                  onChange={() => setExceptionLevel('FunctionalLevel')}
                  disabled={!selectedLevelId}
                />
                <span>Functional Level : {selectedLevelId ? functionalLevelDescription : 'No level selected'}</span>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: selectedRequirements.size === 0 ? 0.6 : 1
              }}>
                <input
                  type="radio"
                  name="exceptionLevel"
                  style={{ width: '20px', height: '20px' }}
                  checked={exceptionLevel === 'Requirement'}
                  onChange={() => setExceptionLevel('Requirement')}
                  disabled={selectedRequirements.size === 0}
                />
                <span>
                  {selectedRequirements.size === 0 ? (
                    'Requirement : No requirement selected'
                  ) : selectedRequirements.size === 1 ? (
                    `Requirement : ${requirementIdWithPrefix}`
                  ) : (
                    `Requirements : ${selectedRequirements.size} requirements selected`
                  )}
                </span>
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold' }}>Exception Type</label>
              <div style={{ position: 'relative', width: '50%' }}>
                <select
                  value={exceptionType}
                  onChange={(e) => setExceptionType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb',
                    fontSize: '14px'
                  }}
                >
                  <option value="ONLY APPLIES TO">Only Applies To</option>
                  <option value="DOES NOT APPLY TO">Does Not Apply To</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold' }}>Exception Category</label>
              <div style={{ position: 'relative', width: '50%' }}>
                <select
                  ref={categorySelectRef}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category.attribute_category} value={category.attribute_category}>
                      {category.attribute_category}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold' }}>Exception Value</label>
              <div style={{
                width: '50%',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                maxHeight: '240px',
                overflowY: 'auto'
              }}>
                {attributeValues.length > 0 ? (
                  attributeValues.map(attribute => (
                    <div
                      key={attribute.key_attribute_list_id || attribute.attribute_name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{ width: '20px', height: '20px', marginRight: '12px' }}
                        checked={selectedAttributes.some(attr => {
                          const selectedId = attr.key_attribute_list_id !== undefined && attr.key_attribute_list_id !== 0
                            ? attr.key_attribute_list_id
                            : attr.attribute_name;
                          const currentId = attribute.key_attribute_list_id !== undefined && attribute.key_attribute_list_id !== 0
                            ? attribute.key_attribute_list_id
                            : attribute.attribute_name;
                          return selectedId === currentId;
                        })}
                        onChange={() => toggleAttributeSelection(attribute)}
                      />
                      <span>{attribute.attribute_name}</span>
                    </div>
                  ))
                ) : (
                  <div style={{
                    padding: '12px',
                    color: '#6b7280',
                    textAlign: 'center'
                  }}>
                    {selectedCategory
                      ? "No values available for this category"
                      : "Select a category to see available values"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Exception'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateExceptionDialog;